import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback
} from 'react';
import { IPlacesModel } from '../modals/activity/use-add-activity';
import { ITripFolder } from '../../Models/trips/trip-folder.model';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { ISearchBiasModel } from '../../Models/search-bias.model';

// Unified modal types
export type ModalType =
  | 'add'
  | 'addFromImage'
  | 'edit'
  | 'delete'
  | 'publish'
  | 'copy'
  | 'move'
  | 'moveToFolder'
  | 'copyToFolder'
  | 'moveFolderToTrip'
  | 'tripInfo'
  | 'comment';
export type ModalCategory =
  | 'folder'
  | 'activity'
  | 'destination'
  | 'day'
  | 'photo'
  | 'itinerary'
  | 'trip';

// Generic modal state interface
export interface BaseModalState<T = any> {
  show: boolean;
  modalType: ModalType | null;
  category: ModalCategory | null;
  place?: IPlacesModel | null;
  bias?: ISearchBiasModel | null;
  data?: T | null; // For passing existing data (edit operations)
  isLoading?: boolean;
  error?: string | null;
  onSuccess?: (() => void) | null;
}

// Specific data types for each category
export interface FolderModalData extends ITripFolder {}
/**
 * ActivityModalData is a partial activity shape used for modal context.
 * displayState is optional here because modals may be opened with partial data
 * (e.g., copy/move/delete where only id + title + type are known).
 */
export interface ActivityModalData extends Omit<ITripActivity, 'displayState'> {
  displayState?: ITripActivity['displayState'];
}
export interface DestinationModalData extends ITripFolder {}
export interface DayModalData {
  id?: string;
  title?: string;
  description?: string;
  color?: string;
  day?: string;
}
export interface PhotoModalData {
  tripId?: string;
}
export interface ItineraryModalData {
  id?: string;
  title?: string;
  description?: string;
}

// Context type
interface TripModalContextType {
  modalState: BaseModalState;

  // Generic modal operations
  openModal: <T = any>(
    category: ModalCategory,
    modalType: ModalType,
    options?: {
      place?: IPlacesModel;
      bias?: ISearchBiasModel;
      data?: T;
    }
  ) => void;

  closeModal: () => void;
  setModalLoading: (loading: boolean) => void;
  setModalError: (error: string | null) => void;

  // Convenience methods for specific modals
  openFolderModal: (
    type: ModalType,
    options?: {
      place?: IPlacesModel;
      bias?: ISearchBiasModel;
      folderData?: FolderModalData;
    }
  ) => void;

  openActivityModal: (
    type: ModalType,
    options?: {
      place?: IPlacesModel;
      bias?: ISearchBiasModel;
      activityData?: ActivityModalData;
      onSuccess?: () => void;
    }
  ) => void;

  openDestinationModal: (
    type: ModalType,
    options?: {
      place?: IPlacesModel;
      bias?: ISearchBiasModel;
      destinationData?: DestinationModalData;
    }
  ) => void;

  openDayModal: (
    type: ModalType,
    options?: {
      dayData?: DayModalData;
    }
  ) => void;

  openPhotoModal: (
    type: ModalType,
    options?: {
      photoData?: PhotoModalData;
      bias?: ISearchBiasModel;
    }
  ) => void;

  openItineraryModal: (
    type: ModalType,
    options?: {
      itineraryData?: ItineraryModalData;
    }
  ) => void;

  // Type-safe getters
  getFolderData: () => FolderModalData | null;
  getActivityData: () => ActivityModalData | null;
  getDestinationData: () => DestinationModalData | null;
  getDayData: () => DayModalData | null;
  getPhotoData: () => PhotoModalData | null;
  getItineraryData: () => ItineraryModalData | null;
}

const TripModalContext = createContext<TripModalContextType | undefined>(
  undefined
);

export const TripModalProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [modalState, setModalState] = useState<BaseModalState>({
    show: false,
    modalType: null,
    category: null,
    place: null,
    bias: null,
    data: null,
    isLoading: false,
    error: null
  });

  // Generic modal operations
  const openModal = useCallback(
    <T = any,>(
      category: ModalCategory,
      modalType: ModalType,
      options: {
        place?: IPlacesModel;
        bias?: ISearchBiasModel;
        data?: T;
        onSuccess?: () => void;
      } = {}
    ) => {
      setModalState({
        show: true,
        category,
        modalType,
        place: options.place || null,
        bias: options.bias || null,
        data: options.data || null,
        isLoading: false,
        error: null,
        onSuccess: options.onSuccess || null
      });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModalState({
      show: false,
      modalType: null,
      category: null,
      place: null,
      bias: null,
      data: null,
      isLoading: false,
      error: null
    });
  }, []);

  const setModalLoading = useCallback((loading: boolean) => {
    setModalState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  const setModalError = useCallback((error: string | null) => {
    setModalState((prev) => ({ ...prev, error, isLoading: false }));
  }, []);

  // Convenience methods
  const openFolderModal = useCallback(
    (
      type: ModalType,
      options: {
        place?: IPlacesModel;
        bias?: ISearchBiasModel;
        folderData?: FolderModalData;
      } = {}
    ) => {
      openModal('folder', type, {
        place: options.place,
        bias: options.bias,
        data: options.folderData
      });
    },
    [openModal]
  );

  const openActivityModal = useCallback(
    (
      type: ModalType,
      options: {
        place?: IPlacesModel;
        bias?: ISearchBiasModel;
        activityData?: ActivityModalData;
        onSuccess?: () => void;
      } = {}
    ) => {
      openModal('activity', type, {
        place: options.place,
        bias: options.bias,
        data: options.activityData,
        onSuccess: options.onSuccess
      });
    },
    [openModal]
  );

  const openDestinationModal = useCallback(
    (
      type: ModalType,
      options: {
        place?: IPlacesModel;
        bias?: ISearchBiasModel;
        destinationData?: DestinationModalData;
      } = {}
    ) => {
      openModal('destination', type, {
        place: options.place,
        bias: options.bias,
        data: options.destinationData
      });
    },
    [openModal]
  );

  const openDayModal = useCallback(
    (
      type: ModalType,
      options: {
        dayData?: DayModalData;
      } = {}
    ) => {
      openModal('day', type, {
        data: options.dayData
      });
    },
    [openModal]
  );

  // Type-safe getters
  const getFolderData = useCallback((): FolderModalData | null => {
    return modalState.category === 'folder'
      ? (modalState.data as FolderModalData)
      : null;
  }, [modalState]);

  const getActivityData = useCallback((): ActivityModalData | null => {
    return modalState.category === 'activity'
      ? (modalState.data as ActivityModalData)
      : null;
  }, [modalState]);

  const getDestinationData = useCallback((): DestinationModalData | null => {
    return modalState.category === 'destination'
      ? (modalState.data as DestinationModalData)
      : null;
  }, [modalState]);

  const getDayData = useCallback((): DayModalData | null => {
    return modalState.category === 'day'
      ? (modalState.data as DayModalData)
      : null;
  }, [modalState]);

  const openPhotoModal = useCallback(
    (
      type: ModalType,
      options: {
        photoData?: PhotoModalData;
        bias?: ISearchBiasModel;
      } = {}
    ) => {
      openModal('photo', type, { data: options.photoData, bias: options.bias });
    },
    [openModal]
  );

  const openItineraryModal = useCallback(
    (
      type: ModalType,
      options: {
        itineraryData?: ItineraryModalData;
      } = {}
    ) => {
      openModal('itinerary', type, { data: options.itineraryData });
    },
    [openModal]
  );

  const getPhotoData = useCallback((): PhotoModalData | null => {
    return modalState.category === 'photo'
      ? (modalState.data as PhotoModalData)
      : null;
  }, [modalState]);

  const getItineraryData = useCallback((): ItineraryModalData | null => {
    return modalState.category === 'itinerary'
      ? (modalState.data as ItineraryModalData)
      : null;
  }, [modalState]);

  const contextValue = useMemo(
    () => ({
      modalState,
      openModal,
      closeModal,
      setModalLoading,
      setModalError,
      openFolderModal,
      openActivityModal,
      openDestinationModal,
      openDayModal,
      openPhotoModal,
      openItineraryModal,
      getFolderData,
      getActivityData,
      getDestinationData,
      getDayData,
      getPhotoData,
      getItineraryData
    }),
    [
      modalState,
      openModal,
      closeModal,
      setModalLoading,
      setModalError,
      openFolderModal,
      openActivityModal,
      openDestinationModal,
      openDayModal,
      openPhotoModal,
      openItineraryModal,
      getFolderData,
      getActivityData,
      getDestinationData,
      getDayData,
      getPhotoData,
      getItineraryData
    ]
  );

  return (
    <TripModalContext.Provider value={contextValue}>
      {children}
    </TripModalContext.Provider>
  );
};

export const useTripModal = () => {
  const context = useContext(TripModalContext);
  if (!context) {
    throw new Error('useTripModal must be used within a TripModalProvider');
  }
  return context;
};

// Type guards for better type safety
export const isFolderModal = (
  modalState: BaseModalState
): modalState is BaseModalState<FolderModalData> => {
  return modalState.category === 'folder';
};

export const isActivityModal = (
  modalState: BaseModalState
): modalState is BaseModalState<ActivityModalData> => {
  return modalState.category === 'activity';
};

export const isDestinationModal = (
  modalState: BaseModalState
): modalState is BaseModalState<DestinationModalData> => {
  return modalState.category === 'destination';
};

export const isDayModal = (
  modalState: BaseModalState
): modalState is BaseModalState<DayModalData> => {
  return modalState.category === 'day';
};

export const isPhotoModal = (
  modalState: BaseModalState
): modalState is BaseModalState<PhotoModalData> => {
  return modalState.category === 'photo';
};

export const isItineraryModal = (
  modalState: BaseModalState
): modalState is BaseModalState<ItineraryModalData> => {
  return modalState.category === 'itinerary';
};

export const isTripModal = (
  modalState: BaseModalState
): modalState is BaseModalState => {
  return modalState.category === 'trip';
};
